import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  EuiButton,
  EuiFieldSearch,
  EuiFieldText,
  EuiFormRow,
  EuiSelect,
  EuiTextArea,
} from '@elastic/eui';

const newDay = number => ({
  scriptureReferences: '',
  title: `第 ${number} 天`,
  summary: '',
});

const ReadingPlanTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [dayErrors, setDayErrors] = useState({});
  const [filters, setFilters] = useState({ search: '', status: 'all' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const reportError = error => {
    const errors = error?.response?.data?.scriptureErrors;
    if (errors?.length) {
      const mapped = Object.fromEntries(errors.map(item => [item.dayNumber - 1, item.message]));
      setDayErrors(mapped);
      setSelectedDayIndex(errors[0].dayNumber - 1);
    }
    setMessage({
      type: 'error',
      text: error?.response?.data?.message || '操作失敗，請稍後再試。',
    });
  };

  const loadDetails = async id => {
    const { data } = await axios.get(`/api/admin/reading-plan-templates/${id}`);
    const days = (data.days || []).map(day => ({
      scriptureReferences: day.scriptureReferences || '',
      title: day.title || '',
      summary: day.summary || '',
    }));
    setSelected(data);
    setForm({
      name: data.name || '',
      description: data.description || '',
      category: data.category || '',
      days: days.length ? days : [newDay(1)],
    });
    setSelectedDayIndex(0);
    setDayErrors({});
    return data;
  };

  const loadTemplates = async (
    requestedPage = page,
    requestedFilters = filters,
    preferredId = selected?.id,
  ) => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/reading-plan-templates', {
        params: {
          page: requestedPage,
          pageSize: 30,
          search: requestedFilters.search || undefined,
          status: requestedFilters.status,
        },
      });
      setTemplates(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || requestedPage);
      const nextId = (data.items || []).some(item => item.id === preferredId)
        ? preferredId
        : data.items?.[0]?.id;
      if (nextId) await loadDetails(nextId);
      else {
        setSelected(null);
        setForm(null);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates(1, filters, null);
  }, []);

  const beginCreate = () => {
    setSelected({ id: null, isActive: true, templateKey: '建立後自動產生' });
    setForm({
      name: '',
      description: '',
      category: '讀經計劃',
      days: [newDay(1)],
    });
    setSelectedDayIndex(0);
    setDayErrors({});
    setMessage(null);
  };

  const updateDay = (field, value) => {
    setForm(current => ({
      ...current,
      days: current.days.map((day, index) => (
        index === selectedDayIndex ? { ...day, [field]: value } : day
      )),
    }));
    if (field === 'scriptureReferences') {
      setDayErrors(current => ({ ...current, [selectedDayIndex]: undefined }));
    }
  };

  const validateDay = async index => {
    const reference = form?.days?.[index]?.scriptureReferences?.trim();
    if (!reference) {
      setDayErrors(current => ({ ...current, [index]: '必須填寫經文範圍。' }));
      return false;
    }
    try {
      const { data } = await axios.post('/api/readingplans/validate-scriptures', {
        references: [reference],
      });
      const item = data.items?.[0];
      if (!item?.isValid) {
        setDayErrors(current => ({ ...current, [index]: item?.message || '經文範圍不正確。' }));
        return false;
      }
      setForm(current => ({
        ...current,
        days: current.days.map((day, dayIndex) => (
          dayIndex === index ? { ...day, scriptureReferences: item.normalized } : day
        )),
      }));
      setDayErrors(current => ({ ...current, [index]: undefined }));
      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  };

  const addDay = () => {
    setForm(current => ({ ...current, days: [...current.days, newDay(current.days.length + 1)] }));
    setSelectedDayIndex(form.days.length);
  };

  const duplicateDay = () => {
    const source = form.days[selectedDayIndex];
    const insertAt = selectedDayIndex + 1;
    setForm(current => ({
      ...current,
      days: [
        ...current.days.slice(0, insertAt),
        { ...source, title: source.title ? `${source.title}（副本）` : `第 ${insertAt + 1} 天` },
        ...current.days.slice(insertAt),
      ],
    }));
    setSelectedDayIndex(insertAt);
    setDayErrors({});
  };

  const removeDay = () => {
    if (form.days.length <= 1) {
      setMessage({ type: 'error', text: '模板至少需要一天。' });
      return;
    }
    if (!window.confirm(`確定刪除第 ${selectedDayIndex + 1} 天嗎？`)) return;
    setForm(current => ({
      ...current,
      days: current.days.filter((_, index) => index !== selectedDayIndex),
    }));
    setSelectedDayIndex(Math.max(0, selectedDayIndex - 1));
    setDayErrors({});
  };

  const saveTemplate = async () => {
    if (!form?.name.trim()) {
      setMessage({ type: 'error', text: '模板名稱不能為空。' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const validation = await axios.post('/api/readingplans/validate-scriptures', {
        references: form.days.map(day => day.scriptureReferences),
      });
      const invalid = (validation.data.items || [])
        .map((item, index) => ({ ...item, index }))
        .filter(item => !item.isValid);
      if (invalid.length) {
        setDayErrors(Object.fromEntries(invalid.map(item => [item.index, item.message])));
        setSelectedDayIndex(invalid[0].index);
        setMessage({ type: 'error', text: '請先修正標示的經文範圍。' });
        return;
      }

      const payload = {
        ...form,
        days: form.days.map((day, index) => ({
          ...day,
          scriptureReferences: validation.data.items[index].normalized,
        })),
      };
      const response = selected.id
        ? await axios.put(`/api/admin/reading-plan-templates/${selected.id}`, payload)
        : await axios.post('/api/admin/reading-plan-templates', payload);
      await loadTemplates(1, filters, response.data.id);
      setMessage({ type: 'success', text: selected.id ? '模板修改已保存。' : '讀經模板已建立。' });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selected?.id || !window.confirm(`確定刪除「${selected.name}」嗎？之後仍可從已刪除清單恢復。`)) return;
    setBusy(true);
    try {
      await axios.delete(`/api/admin/reading-plan-templates/${selected.id}`);
      await loadTemplates(page, filters, null);
      setMessage({ type: 'success', text: '模板已刪除，並已從用戶的套用清單隱藏。' });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const restoreTemplate = async () => {
    if (!selected?.id) return;
    setBusy(true);
    try {
      await axios.post(`/api/admin/reading-plan-templates/${selected.id}/restore`);
      await loadTemplates(page, filters, selected.id);
      setMessage({ type: 'success', text: '模板已恢復到套用清單。' });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const selectedDay = form?.days?.[selectedDayIndex];

  return (
    <div className="reading-plan-template-admin">
      <div className="admin-section-heading">
        <div>
          <h2>讀經模板管理</h2>
          <p>建立及維護一鍵套用的讀經模板；修改不會影響已建立的讀經計劃。</p>
        </div>
        <EuiButton fill onClick={beginCreate}>建立模板</EuiButton>
      </div>

      {message && <div className={`admin-inline-message is-${message.type}`}>{message.text}</div>}

      <form
        className="admin-filter-bar"
        onSubmit={event => {
          event.preventDefault();
          loadTemplates(1, filters, null);
        }}
      >
        <EuiFormRow label="模板名稱或分類">
          <EuiFieldSearch
            value={filters.search}
            onChange={event => setFilters(current => ({ ...current, search: event.target.value }))}
          />
        </EuiFormRow>
        <EuiFormRow label="狀態">
          <EuiSelect
            value={filters.status}
            options={[
              { value: 'all', text: '全部' },
              { value: 'active', text: '使用中' },
              { value: 'deleted', text: '已刪除' },
            ]}
            onChange={event => setFilters(current => ({ ...current, status: event.target.value }))}
          />
        </EuiFormRow>
        <EuiButton type="submit" fill isLoading={loading}>查詢</EuiButton>
      </form>

      <div className="admin-master-detail reading-plan-admin-layout">
        <section className="admin-record-list">
          {templates.map(template => (
            <button
              type="button"
              key={template.id}
              className={`admin-record-card ${selected?.id === template.id ? 'is-selected' : ''}`}
              onClick={() => loadDetails(template.id).catch(reportError)}
            >
              <strong>{template.name}</strong>
              <span>{template.category || '未分類'} · {template.durationDays} 天</span>
              <small>{template.isActive ? '使用中' : '已刪除'} · {template.templateKey}</small>
            </button>
          ))}
          {!loading && !templates.length && <p className="admin-empty-state">沒有符合條件的模板。</p>}
          <div className="admin-pagination-row">
            <EuiButton size="s" disabled={page <= 1 || loading} onClick={() => loadTemplates(page - 1)}>上一頁</EuiButton>
            <span>第 {page} / {totalPages} 頁，共 {total} 個</span>
            <EuiButton size="s" disabled={page >= totalPages || loading} onClick={() => loadTemplates(page + 1)}>下一頁</EuiButton>
          </div>
        </section>

        <section className="admin-editor-panel">
          {!form ? (
            <p className="admin-empty-state">請選擇模板或建立新模板。</p>
          ) : (
            <>
              <div className="admin-detail-title">
                <div>
                  <h3>{selected.id ? selected.name : '建立讀經模板'}</h3>
                  <small>模板代碼：{selected.templateKey}</small>
                </div>
                {selected.id && (
                  <span className={selected.isActive ? 'admin-status active' : 'admin-status archived'}>
                    {selected.isActive ? '使用中' : '已刪除'}
                  </span>
                )}
              </div>

              <div className="reading-plan-admin-form">
                <EuiFormRow label="模板名稱">
                  <EuiFieldText
                    value={form.name}
                    onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                  />
                </EuiFormRow>
                <EuiFormRow label="分類">
                  <EuiFieldText
                    value={form.category}
                    onChange={event => setForm(current => ({ ...current, category: event.target.value }))}
                  />
                </EuiFormRow>
                <EuiFormRow label="簡介" fullWidth>
                  <EuiTextArea
                    fullWidth
                    rows={3}
                    value={form.description}
                    onChange={event => setForm(current => ({ ...current, description: event.target.value }))}
                  />
                </EuiFormRow>
              </div>

              <div className="admin-subsection">
                <div className="admin-section-heading">
                  <div><h4>每日安排（{form.days.length} 天）</h4></div>
                  <div className="admin-editor-actions">
                    <EuiButton size="s" onClick={duplicateDay}>複製本日</EuiButton>
                    <EuiButton size="s" onClick={addDay}>增加一天</EuiButton>
                    <EuiButton size="s" color="danger" onClick={removeDay}>刪除本日</EuiButton>
                  </div>
                </div>
                <div className="reading-plan-admin-days">
                  <div className="reading-plan-admin-day-list">
                    {form.days.map((day, index) => (
                      <button
                        type="button"
                        key={index}
                        className={selectedDayIndex === index ? 'is-selected' : ''}
                        onClick={() => setSelectedDayIndex(index)}
                      >
                        <strong>第 {index + 1} 天</strong>
                        <span>{day.scriptureReferences || '尚未填寫經文'}</span>
                        <small>{dayErrors[index] || day.title || '尚未填寫標題'}</small>
                      </button>
                    ))}
                  </div>
                  <div className="reading-plan-admin-day-editor">
                    <strong>第 {selectedDayIndex + 1} 天</strong>
                    <EuiFormRow
                      label="經文範圍"
                      fullWidth
                      isInvalid={Boolean(dayErrors[selectedDayIndex])}
                      error={dayErrors[selectedDayIndex]}
                    >
                      <EuiFieldText
                        fullWidth
                        value={selectedDay?.scriptureReferences || ''}
                        placeholder="例如：創1-2；太1:1-17"
                        isInvalid={Boolean(dayErrors[selectedDayIndex])}
                        onChange={event => updateDay('scriptureReferences', event.target.value)}
                        onBlur={() => validateDay(selectedDayIndex)}
                      />
                    </EuiFormRow>
                    <EuiFormRow label="標題" fullWidth>
                      <EuiFieldText
                        fullWidth
                        value={selectedDay?.title || ''}
                        onChange={event => updateDay('title', event.target.value)}
                      />
                    </EuiFormRow>
                    <EuiFormRow label="簡介" fullWidth>
                      <EuiTextArea
                        fullWidth
                        rows={4}
                        value={selectedDay?.summary || ''}
                        onChange={event => updateDay('summary', event.target.value)}
                      />
                    </EuiFormRow>
                  </div>
                </div>
              </div>

              <div className="admin-editor-actions">
                <EuiButton fill onClick={saveTemplate} isLoading={busy}>保存模板</EuiButton>
                {selected.id && selected.isActive && (
                  <EuiButton color="danger" onClick={deleteTemplate} disabled={busy}>刪除模板</EuiButton>
                )}
                {selected.id && !selected.isActive && (
                  <EuiButton onClick={restoreTemplate} disabled={busy}>恢復模板</EuiButton>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ReadingPlanTemplateManagement;
